import React, {
  BaseHTMLAttributes,
  Children,
  isValidElement,
  KeyboardEvent,
  PropsWithChildren,
  ReactNode,
  useRef,
  useState,
} from "react"
import { useNavigate } from "react-router-dom"
import Notification from "@components/Notification/Notification"
import { getErrorMessage } from "@/utils/helpers/configs"
import {
  selectAccountBalances,
  selectActiveAccount,
  useLazyLogoutQuery,
  useSetMemberIdMutation,
} from "@/store/apis/linkApi"
import { useSelector } from "react-redux"
import { selectMemberMatrixData } from "@/store/member/member.slice"
import Loader from "@components/Loader/Loader"
import { MatrixContentType, useContentJsx } from "@/utils/hooks/useContent"
import { uniqueId } from "lodash"
import classNames from "classnames"
import { FormikValues, useFormikContext } from "formik"
import { useIsDev } from "@/utils/hooks/useUtil"
import DOMPurify from "dompurify"
import { getLogoutUrl } from "@/utils/helpers/configs"

export const DataError = ({ msg }: { msg: string }) => (
  <code className={"data-error"}>{msg}</code>
)

export const InvestmentsApiError = () => (
  <DataError msg={"Unable to fetch investment data"} />
)

export const PensionPaymentsApiError = () => (
  <DataError msg={"Unable to fetch pension data"} />
)

function ErrorLogoutButton() {
  const [logout] = useLazyLogoutQuery()

  return (
    <button
      className="button"
      onClick={() =>
        logout(true)
          .unwrap()
          .then(() => {
            window.location.href = getLogoutUrl()
          })
      }
      title="Log out of Member Online"
    >
      Log out
    </button>
  )
}

export function GenericErrorMessage() {
  const html = getErrorMessage()
  return (
    <Notification
      notificationType={"alert"}
      className={"generic-error-message"}
    >
      <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html) }}></div>
      <ErrorLogoutButton />
    </Notification>
  )
}

export function FormErrorMessage() {
  const { contentBody } = useContentJsx("errors.forms_error_message")
  return (
    <Notification
      notificationType={"alert"}
      className={"generic-error-message"}
    >
      {contentBody}
    </Notification>
  )
}

type ToggleExpanded = {
  onToggleOpen(): void
  isOpen: boolean
  targetId: string
}

export const ToggleExpanded = ({
  onToggleOpen,
  isOpen,
  targetId,
}: ToggleExpanded) => (
  <button
    className="details-show-btn"
    onClick={onToggleOpen}
    aria-expanded={isOpen}
    aria-controls={targetId}
  >
    <span className="details-show-btn__hidetext">Hide details</span>
    <span className="details-show-btn__showtext">Show details</span>
  </button>
)

export function BackButton({ page, path }: { page?: string; path?: string }) {
  const navigate = useNavigate()

  const action = `${path}` || `-1`

  return (
    <button
      type="button"
      className="button button--gray"
      onClick={() => navigate(action)}
    >
      Back{page ? ` to ${page}` : ""}
    </button>
  )
}

export const PrintButton = ({
  title = `Print`,
  classList = [`print-button`],
}) => (
  <button
    className={classList.join(" ")}
    onClick={() => window.print()}
    title={title}
  >
    {title}
  </button>
)

export const SaveButton = ({ title = `Save` }: { title?: string }) => (
  // title prop lets receipt shells use CMS labels while keeping the save icon
  <button className="save-button" onClick={() => window.print()} title={title}>
    {title}
  </button>
)

export const SubmissionReceivedButtons = () => (
  <div className="submission-received-buttons">
    <PrintButton />
    <SaveButton />
  </div>
)

export const DownloadButton = ({
  title = `Download this page`,
  classList = [`download-button`],
}) => (
  <button className={classList.join(" ")} type="button" title={title}>
    {title}
  </button>
)

type InlineButtonProps = PropsWithChildren<
  Partial<BaseHTMLAttributes<HTMLSpanElement>>
>

export function InlineButton({ children, ...rest }: InlineButtonProps) {
  const ref = useRef<HTMLSpanElement>(null)

  const onKeyDownHandler = (e: KeyboardEvent<HTMLSpanElement>) => {
    if (["Space", "Enter"].includes(e.code)) {
      e.preventDefault()
      ref.current?.click()
    }
  }

  return (
    <span
      ref={ref}
      role="button"
      className="anchor-link"
      tabIndex={0}
      onKeyDown={onKeyDownHandler}
      {...rest}
    >
      {children}
    </span>
  )
}

export function LogoutButton() {
  const [logout] = useLazyLogoutQuery()
  const [setMemberId, { isLoading }] = useSetMemberIdMutation()

  const memberMatrixData = useSelector(selectMemberMatrixData)
  const { isAdmin } = memberMatrixData

  if (isLoading) {
    return <Loader />
  }

  const logoutHandler = () => {
    if (isAdmin) {
      setMemberId(null)
      // Keep modal visible during redirect with longer delay
    } else {
      logout()
        .unwrap()
        .then(() => {
          // Keep modal visible during redirect
          setTimeout(() => {
            window.location.href = getLogoutUrl()
          }, 500)
        })
        .catch(() => {
          // force logout to prevent infinite loop
          window.location.href = getLogoutUrl()
        })
    }
  }

  return (
    <>
      <button
        className="button"
        onClick={logoutHandler}
        title="Log out of Member Online"
      >
        Log out
      </button>
    </>
  )
}

export const MainWrapper = ({ children }: PropsWithChildren) => (
  <main id="main" className="main">
    <div className="content-container">{children}</div>
  </main>
)

type ExpandableContentProps = Pick<PropsWithChildren, "children"> & {
  text: ReactNode
  defaultExpanded?: boolean
  onToggle?: (expanded: boolean) => void
}

export function ExpandableContent({
  children,
  text,
  defaultExpanded = false,
  onToggle,
}: ExpandableContentProps) {
  const [id] = useState(uniqueId("expandable-content"))

  const [expanded, setExpanded] = useState(defaultExpanded)

  const classList = classNames("expandable-content", {
    "expandable-content--active": expanded,
  })

  const handleToggle = () => {
    const next = !expanded
    setExpanded(next)
    onToggle?.(next)
  }

  return (
    <div className={classList}>
      <button
        type={"button"}
        className={"expandable-content__btn"}
        onClick={handleToggle}
        aria-controls={id}
        aria-expanded={expanded}
      >
        {text}
      </button>
      <div
        id={id}
        role="region"
        aria-live="polite"
        aria-expanded={expanded}
        className={"expandable-content__content"}
      >
        {children}
      </div>
    </div>
  )
}

type DebugFormikPayloadProps<Type> = {
  debugFn?(values: Type): void
}

export function DebugFormikPayload<Type extends FormikValues>({
  debugFn = (values: Type) => values,
}: DebugFormikPayloadProps<Type>) {
  const { values } = useFormikContext<Type>()
  const isDev = useIsDev()

  return isDev ? (
    <pre className="debug-formik-payload">
      <p>Current payload (Note: this will only show in local dev)</p>
      <p>{JSON.stringify(debugFn(values), null, 2)}</p>
    </pre>
  ) : null
}

interface DebugFormikErrorsProps {
  showValues?: boolean
}

export function DebugFormikErrors({
  showValues = false,
}: DebugFormikErrorsProps) {
  const { errors, values } = useFormikContext()
  const isDev = useIsDev()

  return isDev ? (
    <pre className="debug-formik-errors">
      <p>Current invalid fields (Note: this will only show in local dev)</p>
      <p>{JSON.stringify(errors, null, 2)}</p>
      {showValues && (
        <>
          <p>Current values</p>
          <p>{JSON.stringify(values, null, 2)}</p>
        </>
      )}
    </pre>
  ) : null
}

type FormHeadingProps = {
  title?: MatrixContentType
  includeSubHeading?: boolean
}

export function FormHeading({
  title,
  includeSubHeading = true,
}: FormHeadingProps) {
  const accounts = useSelector(selectAccountBalances)

  const { planName, accountNumber } = useSelector(selectActiveAccount)

  const resolveChild = (carry: ReactNode, item: ReactNode): ReactNode => {
    if (carry) {
      return carry
    }

    if (
      isValidElement(item) &&
      typeof item.type === "string" &&
      /h[1-4]/.test(item.type)
    ) {
      return item.props.children
    }

    if (isValidElement(item) && item) {
      return Children.toArray((item.props as any)?.children).reduce(
        resolveChild,
        null
      )
    }
  }

  const heading = "string" === typeof title ? title : resolveChild(null, title)

  if (accounts.length > 1) {
    return (
      <h1>
        {heading}
        {includeSubHeading && (
          <span className="form-sub-heading">
            {planName} {accountNumber}
          </span>
        )}
      </h1>
    )
  }

  return <h1>{heading}</h1>
}
